import React, {useEffect, useMemo, useRef, useState} from "react";
import {ASCENDING, CURRENCY, DATE, DESCENDING, NUMBER, STRING_LINK, UNSORTED} from "./constants";
import {TableHead} from "./TableHead";
import {TableBody} from "./TableBody";
import {TableFooter} from "./TableFooter";
import {View} from "./classes/view/View";
import {Row} from "./classes/Row";
import {Cell} from "./classes/Cell";
import "./style.css";
import Filter from "./classes/Filter";
import {isEmpty, Client} from "weed-js";
import TableControls from "./TableControls";


const setDefaultStyle = (columnWidths, viewColumnDefs, collapsedColumns) => {
    const style = (index) => {
        const minWidth = Math.max(columnWidths[index] || 70, 70); // Ensure a minimum width of 70px
        const width = Math.max(minWidth, columnWidths[index] || 70); // Ensure a minimum width of 70px
        return {
            width: columnWidths[index] ? `${width}px` : "auto",
            minWidth: columnWidths[index] ? `${minWidth}px` : "auto",
            maxWidth: columnWidths[index] ? `${columnWidths[index]}px` : "auto",
            whiteSpace: "nowrap"
        }
    }; // Set width based on longest row

    viewColumnDefs.forEach((columnDef, index) => {
        if (collapsedColumns[index]) {
            columnDef.setStyle({
                width: "0px",
                minWidth: "0px",
                maxWidth: "0px",
                whiteSpace: "nowrap",
            });
            return; // Skip further processing for this column
        }
        columnDef.setStyle(style(index)); // Update the columnDef with the calculated style
    });
}

const setCompactStyle = (columnWidths, viewColumnDefs, collapsedColumns) => {
    const style = (index) => {
        const minWidth = Math.max(columnWidths[index] || 50, 50); // Ensure a minimum width of 70px
        const width = Math.max(minWidth, columnWidths[index] || 50); // Ensure a minimum width of 70px
        return {
            width: columnWidths[index] ? `${width}px` : "auto",
            minWidth: columnWidths[index] ? `${minWidth}px` : "auto",
            maxWidth: columnWidths[index] ? `${columnWidths[index]}px` : "auto",
            whiteSpace: "wrap"
        }
    }; // Set width based on longest row

    viewColumnDefs.forEach((columnDef, index) => {
        if (collapsedColumns[index]) {
            columnDef.setStyle({
                width: "0px",
                minWidth: "0px",
                maxWidth: "0px",
                whiteSpace: "nowrap",
            });
            return; // Skip further processing for this column
        }
        columnDef.setStyle(style(index)); // Update the columnDef with the calculated style
    });

}


const getHandleSortClick = (setSortStack) => {
    return (columnDef, evt) => {
        evt.stopPropagation(); // Prevent event bubbling
        setSortStack((prevStack) => {
            const prevState = prevStack.find(item => item.columnDef.getIndex() === columnDef.getIndex());
            let newStack = prevStack.filter((item) => item.columnDef.getIndex() !== columnDef.getIndex());
            if (!prevState) {
                // If the column is not in the stack, add it with ascending direction
                newStack.push({
                    columnDef: columnDef, direction: DESCENDING
                });
                return newStack;
            }

            const newDirection = (prevState.direction + 1) % 3;
            if (newDirection !== UNSORTED) {
                newStack.push({
                    columnDef: columnDef, direction: newDirection
                });
            }
            return newStack;
        });
    }
};

const getHandleColumnFilterClick = (setShowFilterUI) => {
    return (columnDef, evt) => {
        evt.stopPropagation(); // Prevent event bubbling
        setShowFilterUI((prev) => {
            const newShowFilterUI = [...prev];
            newShowFilterUI[columnDef.getIndex()] = !newShowFilterUI[columnDef.getIndex()];
            return newShowFilterUI;
        });
    }
};

const getHandleColumnFilterChange = (setFilterStates) => {
    return (columnDef, filters) => {
        setFilterStates((prevFilters) => {
            // if (isEmpty(filters?.operand) || isEmpty(filters?.operation)) {
            //     return prevFilters.map((filter, index) => index === columnDef.index ? null : filter);
            // }
            const newFilters = [...prevFilters];
            newFilters[columnDef.getIndex()] = filters;
            return newFilters;
        });
    }
};


const applyFilters = (rows, columnDefs, filterStates) => {
    if (isEmpty(rows)) {
        return [];
    }
    if (isEmpty(columnDefs)) {
        return rows; // Return empty array if rows or columnDefs are empty
    }
    if (filterStates.every(filterState => filterState === null)) {
        return rows;
    }

    const filters = columnDefs.map((colDef) => {
        const filterState = filterStates[colDef.getIndex()];
        if (filterState === null || filterState === undefined) {
            return null; // No filter applied for this column
        }
        return new Filter(colDef, filterStates[colDef.getIndex()]);
    }).filter(filter => filter !== null && filter !== undefined);

    const filtered = rows.filter(row => {
        for (const filter of filters) {
            const cell = row.cells[filter.columnDef.getIndex()];
            if (!filter.filter(cell)) {
                return false;
            }
        }
        return true; // If no filters are applied, include the row
    });

    return filtered;
}

const castToRows = (data, columnDefs) => {
    if (isEmpty(data) || isEmpty(columnDefs)) {
        return []; // Return empty array if data or columnDefs are empty
    }
    return data.map((rowData, row) => {
        return new Row(rowData.map((cellData, col) => {
            // Create Cell instances for each row data
            return new Cell(columnDefs[col], cellData, row);
        }), row);
    });
};


const sortView = (view, grouped, columnDefs, sortStack) => {
    if (isEmpty(grouped) || isEmpty(columnDefs)) {
        return []; // Return empty array if grouped or columnDefs are empty
    }
    view.setRenderOrder();
    return sortStack.length > 0 ? grouped.sort((rowA, rowB) => {
        // Sort columnDefs by their sortOrder:
        for (let i = 0; i < sortStack.length; i++) {
            const {columnDef, direction} = sortStack[i];
            const cellA = rowA.cells[columnDef.getRenderOrder()];
            const cellB = rowB.cells[columnDef.getRenderOrder()];
            const cellAValue = cellA?.getSortValue();
            const cellBValue = cellB?.getSortValue();
            const cellAEmpty = cellAValue === null || cellAValue === undefined;
            const cellBEmpty = cellBValue === null || cellBValue === undefined;

            if (direction === ASCENDING) {
                // Compare the sort values of the cells
                if (cellAEmpty || cellBEmpty) {
                    if (cellAEmpty && !cellBEmpty) {
                        return 1; // cellB is greater, so it comes first
                    }
                    if (!cellAEmpty && cellBEmpty) {
                        return -1; // cellA is greater, so it comes first
                    }
                    if (cellAEmpty && cellBEmpty) {
                        continue;
                    }
                }
                if (cellAValue < cellBValue) return -1;
                if (cellAValue > cellBValue) return 1;
            } else if (direction === DESCENDING) {
                if (cellAEmpty || cellBEmpty) {
                    if (cellAEmpty && !cellBEmpty) {
                        return -1; // cellB is less, so it comes first
                    }
                    if (!cellAEmpty && cellBEmpty) {
                        return 1; // cellA is less, so it comes first
                    }
                    if (cellAEmpty && cellBEmpty) {
                        continue;
                    }
                }
                if (cellAValue < cellBValue) return 1;
                if (cellAValue > cellBValue) return -1;
            }
        }
        return 0; // If all columns are equal
    }) : grouped.sort((rowA, rowB) => {
        // If no sort stack is provided, sort by original order (row number)
        return rowA.originalOrder - rowB.originalOrder;
    });
}


const getLongestPossibleRow = (rows, viewColumnDefs) => {
    if (isEmpty(rows) || isEmpty(viewColumnDefs)) {
        return new Row([]); // Return an empty Row if no rows or columnDefs are provided
    }
    const longestCells = viewColumnDefs.map(c => c.getSentinelCell());
    const longestLengths = longestCells.map(cell => cell.getLength());

    const numRows = rows.length;
    const numCols = viewColumnDefs.length;

    for (let i = 0; i < numRows; i++) {
        const cells = rows[i].cells;
        for (let j = 0; j < numCols; j++) {
            const cell = cells[j];
            if (!cell) {
                continue;
            }
            const len = cell.getLength();
            if (longestLengths[j] < len) {
                longestLengths[j] = len; // Update the longest length for each column
                longestCells[j] = cell; // Update the longest cell for each column
            }
        }
    }

    return new Row(longestCells, 0); // Create a new Row with the longest cells
}

const getTableColumnWidths = (longestRow, compact, viewColumnDefs) => {
    const tableElement = document.createElement("table");
    tableElement.className = "canold-table";
    tableElement.style.display = "block"; // Ensure the table is rendered
    tableElement.style.position = "absolute"; // Position it off-screen
    tableElement.style.left = "-9999px"; // Move it off-screen
    tableElement.style.top = "-9999px"; // Move it off-screen
    tableElement.style.visibility = "hidden"; // Hide the table to avoid flickering

    // Header
    const thead = document.createElement("thead");
    thead.className = "canold-table-thead";
    const theadTr = document.createElement("tr");
    theadTr.className = "canold-table-head-tr";
    longestRow.cells.forEach(cell => {
        const th = document.createElement("th");
        th.className = "canoldl-table-th";
        th.innerHTML = cell.columnDef.displayName;
        theadTr.appendChild(th);
    });
    thead.appendChild(theadTr);

    // Body
    const tbody = document.createElement("tbody");
    tbody.className = "canold-table-tbody";

    const tr = document.createElement("tr");
    longestRow.cells.forEach((cell) => {
        tr.appendChild(cell.renderAsDOMElement(compact, viewColumnDefs));
    });
    tbody.appendChild(tr);

    // Force the browser to calculate the widths
    tableElement.appendChild(thead);
    tableElement.appendChild(tbody);

    document.body.appendChild(tableElement);
    const widths = Array.from(tableElement.querySelectorAll("th")).map(th => th.offsetWidth);
    document.body.removeChild(tableElement);
    return widths;
}


export const Table = ({isLoading, columnDefs, data, onViewWindowChange, setActiveView, savedView, setSavedView, additionalActions=null, noDataMessage="No data available."}) => {

    useEffect(() => {
        columnDefs.forEach((columnDef, index) => {
            if (columnDef.getIndex() === null) {
                columnDef.setIndex(index);
                columnDef.setRenderOrder(index);
            }
        });

    }, []);  // I think this should only run once, so we need to provide an empty list.

    const [sortStack, setSortStack] = useState([]);
    const [filterStates, setFilterStates] = useState(columnDefs.map(() => null));
    const [showFilterUI, setShowFilterUI] = useState(columnDefs.map(() => false));
    const tableRef = useRef(null);
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(0);
    const [compact, setCompact] = useState(true);
    const [showViewMenu, setShowViewMenu] = useState(false);
    const [viewGeneration, setViewGeneration] = useState(0); // no-qa
    const [collapsedColumns, setCollapsedColumns] = useState([]);

    const view = useRef(new View({
        columnDefs: columnDefs,
        rows: [],
        setGeneration: setViewGeneration,
    }));

    useEffect(() => {
        setActiveView && setActiveView(savedView || view.current);
        setSavedView && setSavedView(savedView || view.current);
    }, [view, savedView]);


    const offset = page * pageSize;
    const rows = useMemo(() => castToRows(data, (savedView || view.current).columnDefs), [data, view, savedView, columnDefs]);
    const filtered = useMemo(() => applyFilters(rows, (savedView || view.current).getActiveColumnDefs(), filterStates), [
        rows, filterStates, columnDefs, savedView, view
    ]);

    let replayed = false;
    let grouped = useMemo(() => {
        if ((isEmpty(view.current) && isEmpty(savedView)) || isEmpty(columnDefs)) {
            return filtered; // Return empty array if view or columnDefs are empty
        }
        // When the data changes we need to replay the whole view. Otherwise we can just run `getViewableRows()` after
        // `applyAll()`
        replayed = true;
        return (savedView || view.current).setRows(filtered).replay().getViewableRows();
    }, [filtered]);

    grouped = useMemo(() => {
        // If we have replayed the view, we can just return the grouped rows
        return replayed ? grouped : (savedView || view.current).getViewableRows() || grouped;
    }, [filtered, viewGeneration]);

    // We need to replay only when the user removes a state change that has already been applied, which the view already handles.

    const sorted = useMemo(
        () => sortView(savedView || view.current, grouped, columnDefs, sortStack),
        [grouped, sortStack, columnDefs]
    );

    sorted.forEach((row, index) => {
        row.setRowNumber(index); // Update the row number after sorting
    });

    useEffect(() => {
        if (isEmpty(onViewWindowChange)) return;
        onViewWindowChange(sorted.slice(offset, offset + pageSize));
    }, [offset, pageSize, sorted, columnDefs]);

    const viewColumnDefs = (savedView || view.current).getActiveColumnDefs() || columnDefs;
    const columnWidths = useMemo(() => {
        const longest = getLongestPossibleRow(sorted, viewColumnDefs);
        return getTableColumnWidths(longest, compact, viewColumnDefs);
    }, [sorted, columnDefs, compact]);

    compact ? setCompactStyle(columnWidths, viewColumnDefs, collapsedColumns) : setDefaultStyle(columnWidths, viewColumnDefs, collapsedColumns);

    return <div className={"canold-table-container"}>
        <TableControls
            columnDefs={viewColumnDefs}
            compact={compact}
            makeCompact={() => setCompact(!compact)}
            page={page}
            pageSize={pageSize}
            rows={sorted}
            setPage={setPage}
            setPageSize={setPageSize}
            setShowViewMenu={setShowViewMenu}
            showViewMenu={showViewMenu}
            suffix={"upper"}
            totalRows={sorted.length}
            view={savedView || view.current}
            additionalActions={additionalActions}
        />
        <table className="canold-table" cellSpacing={0} cellPadding={0} ref={tableRef} role="canold-table">
            <TableHead
                collapsed={collapsedColumns}
                onFilterChange={getHandleColumnFilterChange(setFilterStates)}
                onFilterClick={getHandleColumnFilterClick(setShowFilterUI)}
                onSortClick={getHandleSortClick(setSortStack)}
                setCollapsed={setCollapsedColumns}
                setShowViewMenu={setShowViewMenu}
                showViewMenu={showViewMenu}
                sortStack={sortStack}
                view={savedView || view.current}
            />
            <TableBody
                noDataMessage={noDataMessage}
                compact={compact}
                data={sorted}
                isLoading={isLoading}
                offset={page * pageSize}
                pageSize={pageSize}
                collapsed={collapsedColumns}
                view={savedView || view.current}
            />
            <TableFooter
                compact={compact}
                makeCompact={() => setCompact(!compact)}
                page={page}
                pageSize={pageSize}
                rows={sorted}
                setPage={setPage}
                setPageSize={setPageSize}
                totalRows={sorted.length}
                view={savedView || view.current}
            />
        </table>
        <TableControls
            columnDefs={viewColumnDefs}
            compact={compact}
            makeCompact={() => setCompact(!compact)}
            page={page}
            pageSize={pageSize}
            rows={sorted}
            setPage={setPage}
            setPageSize={setPageSize}
            setShowViewMenu={setShowViewMenu}
            showViewMenu={showViewMenu}
            suffix={"lower"}
            totalRows={sorted.length}
            view={savedView || view.current}
            additionalActions={additionalActions}
        />
    </div>
};

export default Table;